import { notFound } from "@tanstack/react-router";
import { getWikipediaResourceQueryOptions } from "@/components/wikipedia-resource-query";
import { requireCatalogPageId } from "@/lib/catalog-route-param";
import { COMPANY_DISCOGRAPHY_URL_CONFIG, buildFacetedSearchState } from "@/lib/faceted-search-url";
import { getSingleReleaseSearchFormat } from "@/lib/release-search-format-filter";
import { discographyQueryTypeFromSearchParams } from "@/routes/_app/artist/$id/discography-query-type";
import { redirectCatalogRouteIfNeeded } from "@/utils/catalog-route-redirect";
import { resolveLegacyDiscogsCatalogId } from "@/utils/legacy-id-redirect";
import { prefetchForSsr } from "@/utils/loader";
import { releaseSearchFilterToFilter } from "@garimpa/lib/searchState";
import { getCompanyAlbumsInfiniteQueryOptions } from "./$id/company-albums-query";
import { getCompanyBioResourceQueryOptions } from "./$id/company-bio-resource-query";
import { getCompanyChildCompaniesQueryOptions } from "./$id/company-child-companies-query";
import { getCompanyQueryOptions } from "./$id/company-query";
interface CompanySearch {
  q?: string;
  type?: string;
  sort?: string;
  format?: string;
  genre?: string[];
  style?: string[];
  country?: string;
  label?: string[];
  company?: string[];
  year?: number[];
  filter?: ReleaseSearchFilter;
}
const SplitLoader = async ({
  context: {
    queryClient,
    locale
  },
  params,
  deps,
  location
}) => {
  const search = location.search as CompanySearch;
  const companyId = requireCatalogPageId(params.id);
  const discographyQueryType = discographyQueryTypeFromSearchParams(deps.type);
  const sort = deps.sort as DiscographySort | undefined ?? "popular";
  const normalizedSearch = search.q;
  const state = buildFacetedSearchState(deps, COMPANY_DISCOGRAPHY_URL_CONFIG);
  const releaseFilter = releaseSearchFilterToFilter(state?.filter, {
    allowed: COMPANY_DISCOGRAPHY_URL_CONFIG.keys,
    allTermKeys: COMPANY_DISCOGRAPHY_URL_CONFIG.allKeys
  });
  const activeFormat = getSingleReleaseSearchFormat(state?.filter) ?? "all";
  const company = await queryClient.ensureQueryData(getCompanyQueryOptions({
    id: companyId,
    locale
  }));
  if (!company) {
    const legacyCatalogId = await resolveLegacyDiscogsCatalogId({
      entity: "label",
      id: companyId,
      queryClient
    });
    if (legacyCatalogId) {
      redirectCatalogRouteIfNeeded({
        force: true,
        location,
        targetPath: `/company/${encodeURIComponent(legacyCatalogId)}`
      });
    }
    throw notFound();
  }
  redirectCatalogRouteIfNeeded({
    location,
    targetPath: `/company/${encodeURIComponent(companyId)}`
  });
  const companyAlbumsQueryOptions = getCompanyAlbumsInfiniteQueryOptions({
    companyId,
    discographyQueryType,
    filter: releaseFilter,
    format: activeFormat,
    sort,
    search: normalizedSearch
  });
  const prefetches: Promise<unknown>[] = [queryClient.prefetchQuery(getCompanyBioResourceQueryOptions({
    companyId,
    locale
  })), queryClient.prefetchQuery(getCompanyChildCompaniesQueryOptions(companyId)), queryClient.prefetchInfiniteQuery(companyAlbumsQueryOptions)];
  const shouldUseWikipediaFallback = company.wikidataId != null && !company.profile && !company.profileHtml;
  if (shouldUseWikipediaFallback) {
    prefetches.push(queryClient.prefetchQuery(getWikipediaResourceQueryOptions({
      wikidataId: company.wikidataId!,
      locale
    })));
  }
  await prefetchForSsr(prefetches);
  return {
    company,
    discographyQueryType,
    discographyTypeCounts: company.availableTypeCounts
  };
};
export { SplitLoader as loader };